import { readFileSync } from "node:fs";
import { dirname, relative } from "node:path";
import type { DriftIssue } from "../../types.js";
import { resolveDualRootPath } from "./_resolve-path.js";

const LINK_RE = /\[([^\]]*)\]\(([^)]+)\)/g;

/** Scan scaffold markdown for local links whose target file does not exist. */
export function checkBrokenLinks(
  scaffoldFiles: string[],
  projectRoot: string,
  scaffoldRoot: string
): DriftIssue[] {
  const issues: DriftIssue[] = [];

  for (const filePath of scaffoldFiles) {
    const source = relative(projectRoot, filePath);

    // patterns/INDEX.md links are owned by the index-sync checker, which knows
    // the index's rebuild semantics. Skip it here to avoid double-reporting the
    // same dead entry under two codes.
    if (source.endsWith("patterns/INDEX.md") || source === "INDEX.md") continue;

    let content: string;
    try {
      content = readFileSync(filePath, "utf-8");
    } catch {
      continue;
    }

    const fileDir = dirname(filePath);
    const lines = content.split("\n");
    let inFence = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (line.trim().startsWith("```")) {
        inFence = !inFence;
        continue;
      }
      if (inFence) continue;

      // Strip inline code spans so `[x](y)` inside backticks isn't treated as a link.
      const scanLine = line.replace(/`[^`]+`/g, "");
      LINK_RE.lastIndex = 0;
      let match: RegExpExecArray | null;
      while ((match = LINK_RE.exec(scanLine)) !== null) {
        const target = normalizeLinkTarget(match[2].trim());
        if (!target || isExternalOrAnchor(target)) continue;

        if (!resolveDualRootPath(target, { projectRoot, scaffoldRoot }, {
          fromFileDir: fileDir,
          tryStripScaffoldPrefix: true,
        })) {
          const isPattern = source.includes("patterns/");
          issues.push({
            code: "BROKEN_LINK",
            severity: isPattern ? "warning" : "error",
            file: source,
            line: i + 1,
            message: `Markdown link target does not exist: ${target}`,
          });
        }
      }
    }
  }

  return issues;
}

/** Strip angle-bracket wrapping, link titles, and #/? fragments. */
function normalizeLinkTarget(raw: string): string {
  let target = raw.replace(/^<|>$/g, "").trim();
  const titleSplit = target.match(/^(\S+)(?:\s+["'].+["'])?$/);
  if (titleSplit) target = titleSplit[1];
  return target.replace(/[#?].*$/, "");
}

function isExternalOrAnchor(target: string): boolean {
  return (
    /^https?:\/\//i.test(target) ||
    /^mailto:/i.test(target) ||
    target.startsWith("#")
  );
}
